/** Sub-PR E2 §5.8/5.9 · 功能 modal。
 *
 * AI 呼叫 open_function_page（回 action:navigate）或點「不可對話但白名單」的功能磚時，以 MUI Dialog
 * 內嵌該功能的編輯畫面（表單直接走該功能原本 API·不經過 AI·不進對話紀錄·符合 D1）。
 *
 * 可內嵌白名單 EMBEDDABLE_FUNCS：須與後端 FUNCTION_FORM_FIELDS（tools_system.py §4.9）一致——
 * 新增可內嵌功能時兩邊一起加。不在白名單者維持跳頁（由呼叫端處理）。
 *
 * §5.9：prefill 帶入 + 未儲存確認（頁面經 EmbedContext.setDirty 回報變更旗標）。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box, Dialog, DialogContent, DialogTitle, IconButton, useMediaQuery, useTheme,
  Dialog as ConfirmDialog, DialogActions, DialogContentText, Button,
} from '@mui/material';

import { EmbedProvider } from '../../../../contexts/EmbedContext';
import MyProfilePage from '../../../MyProfilePage';
import ChangePasswordPage from '../../../ChangePasswordPage';
import EeMyResumePage from '../../../EeMyResumePage';
import MyBillsPage from '../../platformBilling/MyBillsPage';

/** 可內嵌功能白名單（func_code → 頁面元件）·須與後端 FUNCTION_FORM_FIELDS 一致。 */
export const EMBEDDABLE_FUNCS: Record<string, React.ComponentType> = {
  my_profile: MyProfilePage,
  change_password: ChangePasswordPage,
  // 我的履歷：§11 改身分證 / 看完整畫面時以 modal 開啟（不帶入任何值；內嵌時隱藏頁首）
  ee_my_resume: EeMyResumePage,
  // 我的租戶帳單：唯讀頁·聊天中「打開帳單頁」以 modal 開啟（不帶入欄位）
  ee_my_bills: MyBillsPage,
};

export const isEmbeddable = (funcCode: string): boolean => funcCode in EMBEDDABLE_FUNCS;

export interface FunctionModalTarget {
  funcCode: string;
  funcName?: Record<string, string> | string;
  prefill?: Record<string, string>;
}

interface Props {
  target: FunctionModalTarget | null;
  title: string;                      // 已由呼叫端以 getI18nValue 取好
  onClose: () => void;
}

const FunctionModal: React.FC<Props> = ({ target, title, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dirtyRef = React.useRef(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const Comp = target ? EMBEDDABLE_FUNCS[target.funcCode] : null;

  const attemptClose = React.useCallback(() => {
    if (dirtyRef.current) setConfirmOpen(true);   // §5.9 未儲存確認
    else onClose();
  }, [onClose]);

  const confirmDiscard = () => { setConfirmOpen(false); dirtyRef.current = false; onClose(); };

  if (!target || !Comp) return null;

  return (
    <>
      <Dialog
        open
        onClose={(_e, reason) => {
          // ✕ / Esc / backdrop 皆先過未儲存確認（§5.9）
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') attemptClose();
        }}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>{title}</span>
          <IconButton onClick={attemptClose} size="small" aria-label={t('common.close', '關閉')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <EmbedProvider value={{
              embedded: true,
              prefill: target.prefill,
              onExit: onClose,
              setDirty: (d) => { dirtyRef.current = d; },
            }}>
              <Comp />
            </EmbedProvider>
          </Box>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>{t('chat.modal.unsavedTitle', '尚未儲存')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('chat.modal.unsavedHint', '尚未儲存，確定關閉？')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('common.cancel', '取消')}</Button>
          <Button color="error" variant="contained" onClick={confirmDiscard}>
            {t('common.confirm', '確定')}
          </Button>
        </DialogActions>
      </ConfirmDialog>
    </>
  );
};

export default FunctionModal;
